import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { getBoostQuote, createBoostCheckout, getMyCredits } from "@/lib/boosts.functions";
import { tagOutboundHref } from "@/lib/outbound";
import { TopupPanel } from "@/components/topup-panel";
import { verifyCheckoutSession } from "@/lib/wallets.functions";
import { HEAT_AED_CENTS, BOOST_STEP_HEAT, formatHeat, formatUsd, heatToCents } from "@/lib/behot";
import { categoryBySlugQuery, categoryBoardQuery, type Category } from "@/lib/queries";
import { supabase } from "@/integrations/supabase/client";
import { buildPageMetadata } from "@/lib/seo";
import { analytics, rememberPendingCheckout } from "@/lib/analytics";
import { useT } from "@/i18n";

type BoostSearch = {
  listing?: string | undefined;
  category?: string | undefined;
  checkout?: string | undefined;
  heat?: number | undefined;
};

export const Route = createFileRoute("/boost")({
  validateSearch: (search: Record<string, unknown>): BoostSearch => ({
    listing: typeof search["listing"] === "string" ? search["listing"] : undefined,
    checkout: typeof search["checkout"] === "string" ? search["checkout"] : undefined,
    category: typeof search["category"] === "string" ? search["category"] : undefined,
    heat: Number.isFinite(Number(search["heat"])) && Number(search["heat"]) > 0
      ? Number(search["heat"])
      : undefined,
  }),
  head: () =>
    buildPageMetadata({
      path: "/boost",
      title: "Add Heat — Boost a Listing Up the Board | BeHot.Now",
      description:
        "Add Heat to any BeHot.Now listing and push it up the live board. Pay once and stay hot for the full boost window.",
      ogTitle: "Add Heat — boost a listing",
      ogDescription: "Pay once. Climb the board. Get seen.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Boost", path: "/boost" },
      ],
    }),
  component: BoostPage,
});

const PENDING_KEY = "behot:pending-boost";

type PendingBoost = { listing: string; category: string; amountCents: number };

function rememberPendingBoost(pending: PendingBoost) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
}

function clearPendingBoost() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(PENDING_KEY);
}

function readPendingBoost(listing: string, category: string): PendingBoost | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingBoost;
    if (parsed.listing !== listing || parsed.category !== category) return null;
    if (!Number.isFinite(parsed.amountCents) || parsed.amountCents <= 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

function BoostPage() {
  const { t } = useT();
  const { listing, category, checkout, heat: targetHeat } = Route.useSearch();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const quoteFn = useServerFn(getBoostQuote);
  const checkoutFn = useServerFn(createBoostCheckout);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resuming, setResuming] = useState(checkout === "topup");
  const [useReferral, setUseReferral] = useState(true);
  const [useTopup, setUseTopup] = useState(true);
  const creditsFn = useServerFn(getMyCredits);
  const verifyFn = useServerFn(verifyCheckoutSession);
  const resumed = useRef(false);

  const { data: wallets } = useQuery({
    queryKey: ["my-wallets"],
    enabled: Boolean(user),
    queryFn: () => creditsFn(),
  });

  const { data: myListings = [] } = useQuery({
    queryKey: ["my-listings-boost", user?.id],
    enabled: Boolean(user),
      queryFn: async (): Promise<{ id: string; name: string; slug: string; categoryIds: string[] }[]> => {
      const { data, error: err } = await supabase
        .from("listings")
        .select("id,name,slug,listing_categories(category_id)")
        .eq("owner_user_id", user!.id)
        .order("created_at", { ascending: false });
      if (err) throw err;
      return (data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        slug: row.slug,
        categoryIds: (row.listing_categories ?? []).map(
          (lc: { category_id: string }) => lc.category_id,
        ),
      }));
    },
  });

  const { data: quote, error } = useQuery({
    queryKey: ["boost-quote", listing, category],
    enabled: Boolean(listing && category),
    retry: false,
    queryFn: () => quoteFn({ data: { listingSlug: listing!, categorySlug: category! } }),
  });

  // Every bid is accepted: 3 Heat gets you on any board, then any whole Heat amount.
  const floorCents = quote ? quote.fanMinimumCents : 0;
  const floorHeat = Math.max(1, Math.round(floorCents / HEAT_AED_CENTS) || BOOST_STEP_HEAT);

  // Live board standings, so we can show the position this bid would take.
  const categoryQuery = useQuery({
    ...categoryBySlugQuery(category ?? ""),
    enabled: Boolean(category),
  });
  const categoryRow = categoryQuery.data as Category | null | undefined;
  const { data: boardRows = [] } = useQuery(categoryBoardQuery(categoryRow?.id));

  useEffect(() => {
    if (!quote || amount) return;
    const wanted = targetHeat ?? 0;
    setAmount(String(Math.max(wanted, Math.ceil(floorCents / HEAT_AED_CENTS))));
  }, [quote, amount, floorCents, targetHeat]);

  // Back from a card top-up: confirm the payment, then place the boost the
  // user was in the middle of — they never have to start the flow again.
  useEffect(() => {
    if (checkout !== "topup" || resumed.current || !user || !listing || !category) return;
    resumed.current = true;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    const pending = readPendingBoost(listing, category);

    void (async () => {
      try {
        if (sessionId) await verifyFn({ data: { sessionId } }).catch(() => undefined);
        toast.success(t("flow.boost.toast.topupReceived"));
        if (!pending) return;
        const res = await checkoutFn({
          data: {
            listingSlug: listing,
            categorySlug: category,
            amountCents: pending.amountCents,
            useReferral: true,
            useTopup: true,
            origin: window.location.origin,
          },
        });
        clearPendingBoost();
        toast.success(t("flow.boost.toast.autoCompleted"));
        window.location.href = tagOutboundHref(res.url);
        return;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t("flow.boost.toast.checkoutError"));
      } finally {
        setResuming(false);
        void navigate({ to: "/boost", search: { listing, category }, replace: true });
      }
    })();
  }, [checkout, listing, category, user, navigate, checkoutFn, verifyFn, t]);


  const wantCents = heatToCents(Number(amount) || 0);

  // Where this bid lands on the board right now.
  const myHeatCents = boardRows
    .filter((r) => r.listing.slug === listing)
    .reduce((sum, r) => sum + Number(r.active_heat_cents), 0);
  const otherHeat = boardRows
    .filter((r) => r.listing.slug !== listing)
    .map((r) => Number(r.active_heat_cents))
    .sort((a, b) => b - a);
  const projectedCents = myHeatCents + wantCents;
  const projectedRank = otherHeat.filter((h) => h > projectedCents).length + 1;
  const aboveCents = otherHeat.filter((h) => h > projectedCents).pop() ?? projectedCents;
  const leaderCents = otherHeat[0] ?? 0;
  const nextStepCents = heatToCents(
    Math.floor(Math.max(0, aboveCents - projectedCents) / HEAT_AED_CENTS) + 1,
  );

  const walletCoverCents =
    (useReferral ? (wallets?.referralCents ?? 0) : 0) +
    (useTopup ? (wallets?.topupCents ?? 0) : 0);
  const shortfallCents = Math.max(0, wantCents - walletCoverCents);

  // Came from "Take this position" without picking a listing yet — or the
  // listing in the link no longer exists, so let the user choose again.
  if ((!listing || error || quote === null) && category) {
    const boardName = categoryRow?.name ?? "this board";
    const matching = categoryRow
      ? myListings.filter((l) => l.categoryIds.includes(categoryRow.id))
      : myListings;
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <h1 className="font-display text-2xl font-bold">Pick the listing for this position</h1>
        <p className="mt-2 text-muted-foreground">
          {targetHeat
            ? `You need about ${targetHeat} Heat to take that position on ${boardName}.`
            : `Choose one of your ${boardName} listings to boost.`}
        </p>
        {matching.length > 0 ? (
          <div className="mt-6 space-y-2">
            {matching.map((l) => (
              <Button
                key={l.id}
                asChild
                variant="outline"
                className="w-full justify-start"
              >
                <Link
                  to="/boost"
                  search={{ listing: l.slug, category, heat: targetHeat }}
                >
                  {l.name}
                </Link>
              </Button>
            ))}
          </div>
        ) : (
          <p className="mt-6 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
            You don&apos;t have a listing in {boardName} yet. Create one to take
            this position.
          </p>
        )}
        <Button asChild variant="hot" className="mt-6">
          <Link to="/get-hot" search={{ category }}>
            {matching.length ? "Add a new listing" : "Create your listing"}
          </Link>
        </Button>
      </div>
    );
  }

  if (!listing || !category) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("flow.boost.pickTitle")}</h1>
        <p className="mt-2 text-muted-foreground">
          {t("flow.boost.pickSubtitle")}
        </p>
        <Button asChild variant="hot" className="mt-6">
          <Link to="/hot">{t("flow.boost.browseBoards")}</Link>
        </Button>
      </div>
    );
  }

  async function handleCheckout() {
    if (!quote) return;
    const cents = heatToCents(Number(amount));
    if (!Number.isFinite(cents) || cents < floorCents) {
      toast.error(t("flow.boost.toast.minimumNow", { amount: formatUsd(floorCents) }));
      return;
    }
    setSubmitting(true);
    analytics.beginCheckout({
      kind: "boost",
      valueUsd: cents / HEAT_AED_CENTS,
      itemId: listing ?? undefined,
      category: category ?? undefined,
    });
    rememberPendingCheckout("boost", cents / HEAT_AED_CENTS);
    try {
      const res = await checkoutFn({
        data: {
          listingSlug: listing!,
          categorySlug: category!,
          amountCents: cents,
          useReferral,
          useTopup,
          origin: window.location.origin,
        },
      });
      window.location.href = tagOutboundHref(res.url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("flow.boost.toast.checkoutError"));
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="font-display text-3xl font-bold sm:text-4xl">
        {t("flow.boost.titlePrefix")} <span className="text-gradient-hot">{t("flow.boost.titleWord")}</span>
      </h1>
      <p className="mt-2 text-muted-foreground">
        {t("flow.boost.subtitle")}
      </p>

      {error && (
        <div className="card-surface mt-6 p-6 text-sm text-destructive">
          {error instanceof Error ? error.message : t("flow.boost.errorFallback")}
        </div>
      )}

      {quote && (
        <div className="card-surface mt-6 space-y-5 p-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("flow.boost.boostingLabel")}
            </p>
            <p className="font-display text-xl font-bold">{quote.listingName}</p>
            <p className="text-sm text-muted-foreground">
              {t("flow.boost.onBoardDuration", { category: quote.categoryName, days: quote.durationDays })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">{t("flow.boost.heatLabel")}</Label>
            <Input
              id="amount"
              type="number"
              inputMode="numeric"
              min={floorHeat}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Every bid is accepted. Start from{" "}
              <strong>{formatHeat(floorCents)} Heat</strong> on any board, then add any whole
              Heat amount (3, 4, 5…) to climb.
            </p>
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
              <p className="font-semibold">
                This bid takes position #{String(projectedRank).padStart(2, "0")} on{" "}
                {quote.categoryName}
              </p>
              <p className="text-muted-foreground">
                {formatHeat(myHeatCents + wantCents)} Heat total
                {leaderCents > myHeatCents + wantCents
                  ? ` — ${formatHeat(nextStepCents)} Heat more takes the position above.`
                  : " — that's the top of this board."}
              </p>
            </div>
            <p className="text-sm font-medium">
              {t("flow.boost.youPay", { amount: formatUsd(heatToCents(Number(amount) || 0)) })}
            </p>
          </div>

          {user && (
            <div className="space-y-3 rounded-lg border border-dashed p-4 text-sm">
              <p className="font-semibold">{t("flow.boost.walletTitle")}</p>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={useReferral}
                  onChange={(e) => setUseReferral(e.target.checked)}
                />
                {t("flow.boost.referralLabel", { amount: formatHeat(wallets?.referralCents ?? 0) })}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={useTopup}
                  onChange={(e) => setUseTopup(e.target.checked)}
                />
                {t("flow.boost.topupLabel", { amount: formatHeat(wallets?.topupCents ?? 0) })}
              </label>
              {shortfallCents > 0 ? (
                <div className="space-y-3 rounded-md bg-muted/60 p-3">
                  <p className="text-sm">
                    {t("flow.boost.walletCoverBefore", {
                      covered: formatHeat(walletCoverCents),
                      wanted: formatHeat(wantCents),
                    })}{" "}
                    <strong>{formatHeat(shortfallCents)} Heat</strong> {t("flow.boost.walletCoverAfter")}
                  </p>
                  <TopupPanel
                    compact
                    beforeCheckout={() =>
                      rememberPendingBoost({
                        listing: listing!,
                        category: category!,
                        amountCents: wantCents,
                      })
                    }
                    suggestUsd={shortfallCents / HEAT_AED_CENTS}
                    returnTo={`/boost?listing=${listing}&category=${category}`}
                  />
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t("flow.boost.walletCovers")}
                </p>
              )}
            </div>
          )}

          {!loading && !user ? (
            <Button
              variant="hot"
              size="lg"
              className="w-full"
              onClick={() => void navigate({ to: "/login" })}
            >
              {t("flow.boost.signInToBoost")}
            </Button>
          ) : (
            <Button
              variant="hot"
              size="lg"
              className="w-full"
              disabled={submitting || resuming || loading || shortfallCents > 0}
              onClick={() => void handleCheckout()}
            >
              <Flame className="size-4" />
              {resuming
                ? t("flow.boost.completingAfterTopup")
                : submitting
                  ? t("flow.boost.placingBoost")
                  : shortfallCents > 0
                    ? t("flow.boost.topupToContinue")
                    : t("flow.boost.spendHeat", { amount: formatHeat(wantCents) })}

            </Button>
          )}

          <p className="text-xs text-muted-foreground">
            {t("flow.boost.footerNote")}
          </p>

        </div>
      )}
    </div>
  );
}
